import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Download, Loader2 } from 'lucide-react';
import { agentKeys } from 'features/agents/hooks';
import { useSandboxSession } from 'features/sandbox/SandboxSessionContext';
import { approvalPath, decideApproval, downloadApprovalArtifact, getApproval, retryApproval } from './api';
import { approvalRefetchInterval } from './approvalPolling';
import { draftReviewPath } from '../sandbox/utils/previewDestination';
import PreviewAsRoles from '../viewAs/PreviewAsRoles';
import { publicationError } from './publicationError';
import { approvalSource } from './approvalSource';
import { isAutomationBranchApproval, isBranchReviewApproval } from './branchReviewScope';
import { formatTime } from 'shared/time/businessTime';
import Markdown from 'shared/ui/Markdown';

export const approvalStatus = (request) => {
  if (request.execution_state === 'failed') return 'Decision saved · needs retry';
  if (request.execution_state === 'running') return 'Applying decision';
  if (request.status === 'approved' && request.execution_state === 'succeeded') return 'Approved · completed';
  if (request.status === 'cancelled' && request.cancellation_reason === 'superseded') return 'Replaced';
  return ({ pending: 'Awaiting approval', approved: 'Approved', rejected: 'Changes requested',
    expired: 'Expired', cancelled: 'Cancelled' })[request.status] || request.status;
};

/** Why a card closed: a newer revision replaced it (and which), or it closed some other way. */
export const closedText = (approval, replacedBy) => {
  if (approval.status === 'cancelled' && approval.cancellation_reason === 'superseded') {
    if (replacedBy?.branch_status === 'published' && !replacedBy?.revision_number) return 'Already published. Nothing is left to approve.';
    return replacedBy?.revision_number
      ? `Replaced by revision ${replacedBy.revision_number}.`
      : 'Replaced by a newer revision.';
  }
  return 'This request is closed. A new request is needed to continue.';
};

function ProposalDetails({ value }) {
  if (value == null) return null;
  if (Array.isArray(value)) return <ul className="list-disc space-y-2 pl-4">{value.map((item, index) => <li key={index}><ProposalDetails value={item} /></li>)}</ul>;
  if (typeof value === 'object') return <dl className="space-y-3">{Object.entries(value).map(([key, item]) => <div key={key}>
    <dt className="text-xs font-semibold capitalize text-slate-500">{key.replaceAll('_', ' ')}</dt>
    <dd className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-700"><ProposalDetails value={item} /></dd>
  </div>)}</dl>;
  return <>{String(value)}</>;
}


/** The immutable connector proposal, including write scope and required local setup. */
export function SystemDecision({ approval }) {
  const proposal = approval.proposal || {};
  const requiredKeys = proposal.local_action?.new_required_setup_fields || [];
  const requiredFields = (proposal.setup_fields || []).filter(field => requiredKeys.includes(field.key));
  return <>
    {requiredFields.length > 0 && <div className="space-y-2 text-sm text-slate-700">
      <p className="font-medium">Required local setup</p>
      {requiredFields.map(field => <div key={field.key}><p>{field.label || field.key}</p>
        {field.help_text && <p className="text-slate-500">{field.help_text}</p>}</div>)}
    </div>}
    <details className="text-sm text-slate-600">
    <summary className="cursor-pointer font-medium">Technical details</summary>
    <dl className="mt-3 space-y-2 rounded-lg bg-slate-50 p-4">
      <div><dt className="text-xs font-semibold text-slate-500">Allowed resources</dt>
        <dd className="mt-1 break-words">{(proposal.resources || []).join(', ') || 'None'}</dd></div>
      {proposal.version_id && <div><dt className="text-xs font-semibold text-slate-500">Definition version</dt>
        <dd className="mt-1 break-all">{proposal.definition_version || proposal.version_id}</dd></div>}
      {proposal.change_summary && <div><dt className="text-xs font-semibold text-slate-500">Configuration changes</dt>
        <dd className="mt-2"><ProposalDetails value={proposal.change_summary} /></dd></div>}
      {proposal.access_scope && <div><dt className="text-xs font-semibold text-slate-500">Read and write scope</dt>
        <dd className="mt-2"><ProposalDetails value={proposal.access_scope} /></dd></div>}
      {proposal.configuration && <div><dt className="text-xs font-semibold text-slate-500">Full configuration for this approval</dt>
        <dd><pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap break-words text-xs">{JSON.stringify(proposal.configuration, null, 2)}</pre></dd></div>}
    </dl>
  </details></>;
}

function ApprovalFiles({ approvalId, artifacts }) {
  const download = useMutation({ mutationFn: (artifact) => downloadApprovalArtifact(approvalId, artifact) });
  if (!artifacts?.length) return null;
  return <div className="space-y-2" aria-label="Review files">
    <p className="text-xs font-semibold text-slate-600">Files for this review</p>
    <ul className="space-y-2">{artifacts.map((artifact) => <li key={artifact.file_artifact_id}>
      <button type="button" disabled={download.isPending} onClick={() => download.mutate(artifact)}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-cyan-800 hover:bg-cyan-50 disabled:opacity-50">
        {download.isPending && download.variables?.file_artifact_id === artifact.file_artifact_id
          ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
        {artifact.filename || artifact.label}
      </button>
    </li>)}</ul>
    {download.error && <p role="alert" className="text-sm text-red-700">{download.error.message}</p>}
  </div>;
}

export default function ApprovalReview({ approvalId, compact = false, embeddedReview = false, redirectPublication = false, workingReviewId, expectedRevisionId, expectedBranchId, decisionsEnabled = true, hidePublicationAction = false }) {
  const queryClient = useQueryClient();
  const { previewSandboxId, clearPreview } = useSandboxSession();
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const query = useQuery({
    queryKey: ['approval', approvalId], queryFn: () => getApproval(approvalId), enabled: Boolean(approvalId),
    refetchInterval: approvalRefetchInterval,
    refetchIntervalInBackground: true,
  });
  const effectApplied = (request) => {
    if (request?.status === 'approved' && request.execution_state === 'succeeded'
        && ['sandbox_publication', 'ontology_schema_publication'].includes(request.kind)
        && request.scope?.sandbox_id === previewSandboxId) clearPreview();
    // An applied effect may change branch previews, Agent state and Views.
    return queryClient.invalidateQueries();
  };
  const mutation = useMutation({
    mutationFn: (decision) => decision === 'retry' ? retryApproval(approvalId) : decideApproval(
      approvalId, decision, reason.trim() || null,
    ),
    onSuccess: async (result) => {
      setRejecting(false);
      const request = result.approval;
      if (request?.execution_state === 'running') {
        // A publication runs after the decision returns. Refetching every page now
        // only competes with it for the same server and reads unchanged data;
        // the effect below refreshes everything once it has applied.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['approval', approvalId] }),
          queryClient.invalidateQueries({ queryKey: agentKeys.inbox() }),
          queryClient.invalidateQueries({ queryKey: ['home'] }),
        ]);
        return;
      }
      await effectApplied(request);
    },
    onError: () => queryClient.invalidateQueries({ queryKey: ['approval', approvalId] }),
  });
  const executionState = query.data?.approval?.execution_state;
  const previousExecutionState = useRef(executionState);
  useEffect(() => {
    const previous = previousExecutionState.current;
    previousExecutionState.current = executionState;
    if (previous === 'running' && ['succeeded', 'failed'].includes(executionState)) {
      void effectApplied(query.data.approval);
    }
    // Only the transition matters; the handler reads the latest card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionState]);
  if (query.isLoading) return <p className="flex items-center gap-2 py-3 text-sm text-slate-500"><Loader2 size={15} className="animate-spin" />Loading approval…</p>;
  if (query.isError) return <p role="alert" className="py-3 text-sm text-red-700">{query.error.message}</p>;
  if (!query.data?.approval) return null;
  const { approval, decisions = [], can_review: canReview, reviewer_user_profile_id: reviewerId } = query.data;
  const systemDecision = approval.kind === 'connector_definition_publication';
  const ownDecision = decisions.find((row) => row.reviewer_user_profile_id === reviewerId);
  const approvedCount = decisions.filter((row) => row.decision === 'approved').length;
  const publication = ['sandbox_publication', 'ontology_schema_publication'].includes(approval.kind);
  const automationBranch = isAutomationBranchApproval(approval);
  const branchReview = isBranchReviewApproval(approval);
  const matchesReview = branchReview
    ? embeddedReview && (approval.scope?.overlay_id === expectedRevisionId || (workingReviewId === approval.id && hidePublicationAction)) && approval.scope?.sandbox_id === expectedBranchId
    : true;
  const summary = typeof approval.proposal?.summary === 'string' ? approval.proposal.summary
    : typeof approval.proposal?.rationale === 'string' ? approval.proposal.rationale : null;
  const canDecide = canReview && approval.status === 'pending' && !ownDecision && decisionsEnabled && matchesReview;
  const busy = mutation.isPending || approval.execution_state === 'running';
  const completed = approval.execution_state === 'succeeded';
  const error = !completed && (mutation.error?.message || (mutation.data?.success === false && mutation.data?.error)
    || (approval.execution_state === 'failed' && approval.execution_result?.error));

  const schemaOnly = approval.kind === 'ontology_schema_publication';
  // A card inside a list of things waiting on the person (Home, Inbox, a chat), not the review itself.
  const listed = compact && !embeddedReview;
  const publicationPath = branchReview && approval.status === 'pending' && canReview && !ownDecision && approval.scope?.sandbox_id
    ? draftReviewPath(approval.scope.sandbox_id, { revisionId: approval.scope.overlay_id, approvalId: approval.id, family: schemaOnly ? 'ontology_schema_delta' : null })
    : null;
  if (publicationPath && redirectPublication && !embeddedReview) return <Navigate replace to={publicationPath} />;
  return <section className={compact ? 'space-y-3' : 'rounded-2xl border border-slate-200 bg-white p-6 shadow-sm'} aria-label="Approval review">
    {embeddedReview && !matchesReview && <p role="alert" className="text-sm text-rose-700">This approval belongs to a different draft. Reopen the review from Needs you.</p>}
    {(!compact || (automationBranch && embeddedReview)) && <>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{approvalSource(approval)}</p>
      <h1 className="mt-2 text-xl font-semibold text-slate-950"><Markdown inline>{approval.title}</Markdown></h1>
      {summary && <Markdown className="mt-3 text-sm leading-6 text-slate-600">{summary}</Markdown>}
      {!publication && !systemDecision && Object.keys(approval.proposal || {}).length > 0 && <details className="mt-4 text-sm text-slate-600">
        <summary className="cursor-pointer font-medium">Technical details</summary>
        <div className="mt-3 rounded-lg bg-slate-50 p-4"><ProposalDetails value={approval.proposal} /></div>
      </details>}
    </>}
    {systemDecision && <SystemDecision approval={approval} />}
    <ApprovalFiles approvalId={approval.id} artifacts={query.data.artifacts} />
    {publicationPath && !schemaOnly && <PreviewAsRoles approval={approval} />}
    {!embeddedReview && !systemDecision && (publicationPath || compact) && <Link className={listed && publicationPath ? 'inline-flex items-center gap-1.5 rounded-lg bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800' : 'inline-flex text-sm font-medium text-cyan-800 underline underline-offset-4'} to={publicationPath || approvalPath(approval.id)}>{publicationPath ? 'Review changes' : 'Open full review'}</Link>}
    {/* A listed card that offers the decision needs no "awaiting" pill; one that can't still says so. */}
    {!(listed && (canDecide || publicationPath) && approval.status === 'pending' && !['failed', 'running'].includes(approval.execution_state) && !(approval.required_decisions > 1)) && <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-600" role="status">
      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium">{approvalStatus(approval)}</span>
      {approval.required_decisions > 1 && <span>{approvedCount} of {approval.required_decisions} approvals</span>}
    </div>}
    {!listed && publication && approval.status === 'pending' && <p className="mt-3 text-sm text-slate-500">{schemaOnly ? 'This approval applies only the data structure changes in this draft. Other changes need separate approval.' : 'Approval applies these changes to your workspace.'}</p>}
    {!listed && automationBranch && approval.status === 'pending' && <p className="mt-3 text-sm text-slate-500">Approval lets the automation continue with this revision. The workflow controls when the changes are published.</p>}
    {ownDecision && approval.status === 'pending' && <p className="mt-3 text-sm text-slate-600">Your decision is recorded. Waiting for the remaining reviewers.</p>}
    {approval.status === 'approved' && approval.execution_state === 'not_required' && <p className="mt-3 text-sm text-slate-600">The decision is available to the {approval.source_kind === 'automation' ? 'Automation' : 'agent'} so it can continue.</p>}
    {['expired', 'cancelled'].includes(approval.status) && <p className="mt-3 text-sm text-slate-600">{closedText(approval, query.data.replaced_by)}</p>}
    {error && <p role="alert" className="mt-3 text-sm text-red-700">{publication ? publicationError(error) : error}</p>}
    {canDecide && <div className="mt-4">
      {rejecting ? <form onSubmit={(event) => { event.preventDefault(); mutation.mutate('rejected'); }}>
        <label className="block text-sm text-slate-700">What should change?
          <textarea className="mt-2 block w-full rounded-lg border border-slate-300 p-3" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={10000} autoFocus rows={3} />
        </label>
        <div className="mt-3 flex gap-2">
          <button disabled={busy} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Request changes</button>
          <button type="button" disabled={busy} onClick={() => setRejecting(false)} className="rounded-lg px-4 py-2 text-sm text-slate-600">Cancel</button>
        </div>
      </form> : <div className="flex flex-wrap gap-2">
        {!(branchReview && hidePublicationAction) && <button type="button" disabled={busy} onClick={() => mutation.mutate('approved')} className="inline-flex items-center gap-2 rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
          {schemaOnly ? 'Approve data structure changes' : publication ? 'Approve and apply' : automationBranch ? 'Approve and continue'
            : approval.kind === 'automation_activation' ? 'Approve and turn on' : approval.kind === 'connector_definition_publication' ? (approval.proposal?.contract_hash ? 'Approve configuration' : 'Approve read list') : 'Approve'}
        </button>}
        <button type="button" disabled={busy} onClick={() => setRejecting(true)} className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 disabled:opacity-50">Request changes</button>
      </div>}
    </div>}
    {canReview && (!embeddedReview || matchesReview) && decisionsEnabled && !(branchReview && hidePublicationAction) && approval.status === 'approved' && approval.execution_state === 'failed' && <div className="mt-4">
      <p className="mb-3 text-sm text-slate-600">Your decision is saved. Retry applying it.</p>
      <button type="button" disabled={busy} onClick={() => mutation.mutate('retry')} className="rounded-lg bg-slate-950 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Retry {publication ? 'applying changes' : 'action'}</button>
    </div>}
    {!compact && decisions.length > 0 && <details className="mt-6 border-t border-slate-100 pt-4 text-sm text-slate-500">
      <summary className="cursor-pointer">Decision history</summary>
      <ul className="mt-3 space-y-3">{decisions.map((row) => <li key={row.id}><span className="font-medium">{row.decision === 'approved' ? 'Approved' : 'Changes requested'}</span> · {formatTime(row.created_at)}{row.reason && <p className="mt-1 whitespace-pre-wrap">{row.reason}</p>}</li>)}</ul>
    </details>}
  </section>;
}
